export type ValueFormat = 'number' | 'integer' | 'percent' | 'currency' | 'duration' | 'text' | 'date';

/**
 * Presentation-only formatting.
 *
 * Every aggregate, rate and total shown in the UI is computed on the server and
 * arrives pre-calculated in the run payload. This module never does arithmetic
 * on report figures — it only renders them. That is what keeps the dashboard,
 * the PDF and the workbook in agreement.
 */
export function formatValue(value: unknown, format: ValueFormat = 'number'): string {
  if (value === null || value === undefined || value === '') return '—';
  if (format === 'text' || format === 'date') return String(value);

  const n = typeof value === 'number' ? value : Number(String(value).replace(/[,£%\s]/g, ''));
  if (Number.isNaN(n)) return String(value);

  switch (format) {
    case 'integer':
      return n.toLocaleString('en-GB', { maximumFractionDigits: 0 });
    case 'percent':
      return `${n.toLocaleString('en-GB', { maximumFractionDigits: 1 })}%`;
    case 'currency':
      return n.toLocaleString('en-GB', { style: 'currency', currency: 'GBP', maximumFractionDigits: 0 });
    case 'duration': {
      const total = Math.round(n);
      const h = Math.floor(total / 3600);
      const m = Math.floor((total % 3600) / 60);
      const s = total % 60;
      if (h) return `${h}h ${String(m).padStart(2, '0')}m`;
      if (m) return `${m}m ${String(s).padStart(2, '0')}s`;
      return `${s}s`;
    }
    default:
      return n.toLocaleString('en-GB', { maximumFractionDigits: 2 });
  }
}

/**
 * Categorical series colours, assigned in FIXED order and never cycled.
 * Reads the CSS custom properties so light and dark stay in one place.
 * A ninth series is not given a generated hue — it falls to muted grey, and the
 * report should fold its tail into "Other" instead.
 */
export function seriesVar(index: number): string {
  return index < 8 ? `var(--s${index + 1})` : 'var(--muted)';
}

export function toNumber(v: unknown): number {
  if (typeof v === 'number') return v;
  if (v === null || v === undefined || v === '') return 0;
  const n = Number(String(v).replace(/[,£$%\s]/g, ''));
  return Number.isNaN(n) ? 0 : n;
}

export function niceCeil(max: number): number {
  if (max <= 0) return 1;
  const magnitude = 10 ** Math.floor(Math.log10(max));
  for (const step of [1, 2, 2.5, 5, 10]) {
    const candidate = step * magnitude;
    if (candidate >= max) return candidate;
  }
  return 10 * magnitude;
}

export function relativeTime(iso: string | null | undefined): string {
  if (!iso) return 'never';
  const then = new Date(iso.includes('T') ? iso : `${iso.replace(' ', 'T')}Z`).getTime();
  if (Number.isNaN(then)) return String(iso);
  const seconds = Math.round((Date.now() - then) / 1000);
  if (seconds < 60) return 'just now';
  if (seconds < 3600) return `${Math.floor(seconds / 60)} min ago`;
  if (seconds < 86400) return `${Math.floor(seconds / 3600)} h ago`;
  if (seconds < 604800) return `${Math.floor(seconds / 86400)} d ago`;
  return new Date(then).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
}

export function formatDateTime(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso.includes('T') ? iso : `${iso.replace(' ', 'T')}Z`);
  if (Number.isNaN(d.getTime())) return String(iso);
  return d.toLocaleString('en-GB', { dateStyle: 'medium', timeStyle: 'short', timeZone: 'Europe/London' });
}
